import { MouseEvent } from "react";
import { MapStateToProps } from "react-redux";
import { Action } from "redux";

import { CHANGE_SCALE_INDEX, DELETE_COMPONENTS, LOAD_PICASSO_STATE, REQUEST_PAINTING_SCROLL_POSITION, SELECT_ALL_COMPONENTS, SELECT_COMPONENT, SELECT_LINK, SELECT_POINT, UNSELECT_ALL, UNSELECT_COMPONENT, UNSELECT_LINK, UNSELECT_POINT, UPDATE_COMPONENT_VISIBILITY, UPDATE_COMPONENTS, UPDATE_COMPONENTS_ALIGNMENT, UPDATE_COMPONENTS_RESIZE_OPTION, UPDATE_LINKS } from "./constants";
import { AlignType, ResizeOptions } from "./enums";
import { IComponent, ILink, IPoint } from "./interfaces";

/** Props sloužící pro předání implementace Moveable komponenty. */
export type MoveablePassableProps = {
    readonly uiComponent: IComponent;
    readonly isSelected: boolean;
    /** Hodnota pro CSS vlastnost grid template. Určuje rozložení gridu picasso komponenty. Je využívána pro korektní zobrazení align-ů. */
    readonly gridTemplate: string;
    /**
     * Vrací JSX komponentu daného @param componentName reprezentující vzhled Moveable komponenty
     * @param componentName Jméno komponenty
     * @param passableProps Props, které jsou poskytnuty Moveable komponentou
     * @returns JSX komponenta daného @param componentName
    */
    readonly renderComponent: (componentName: string, passableProps: MoveablePassableProps) => JSX.Element;
    /**
     * Vrací JSX komponentu daného @param linkName reprezentující vzhled Link komponenty
     * @param linkName Jméno komponenty
     * @param passableProps Props, které jsou poskytnuty Link komponentou
     * @returns JSX komponenta daného @param linkName
    */
    readonly renderLink?: (readonlylinkName: string, passableProps: LinkPassableProps) => JSX.Element;
    readonly onResizePointDown: (e: MouseEvent, selectedAnchors: SelectedAnchorsType) => void;
    readonly onMouseUp: (e: MouseEvent, component: IComponent) => void;
    readonly onMouseClick: (e: MouseEvent, component: IComponent, isSelected: boolean, isSelectable: boolean) => void;
    readonly onMouseLeave: (e: MouseEvent) => void;
    readonly onComponentMouseDown: (e: MouseEvent, isSelected: boolean, isDraggable: boolean, isSelectable: boolean, component: IComponent) => void;
    readonly onMouseMove: (e: MouseEvent, uiComponent: IComponent, isSelected: boolean) => void;
    readonly onLinkChange?: (changedLinkIndex: number, newPoints: readonly IPoint[]) => void;
    readonly wrapMapStateToProps: WrapMapStateToProps<any>;
    readonly clickSelectComponent: (e: MouseEvent) => void;
    readonly mouseDownComponentSelect: (e: MouseEvent) => void;
} & PicassoEditorProps;

/** Props sloužící pro předání implementace Link komponenty. */
export type LinkPassableProps = {
    readonly link: ILink;
    readonly isSelected: boolean;
    readonly isAnyPointSelected: boolean;
    readonly onLinkClick: (e: MouseEvent) => void;
    readonly updatePoints: (newPoints: readonly IPoint[]) => void;
    readonly selectPoint: (pointIndex: number, point: IPoint, lastPosition: IPoint) => void;
} & PicassoEditorProps;

export type ComponentChangeType = {
    readonly componentIndex: number;
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
    readonly deltaX: number;
    readonly deltaY: number;
};

export type LinkChangeType = {
    readonly index: number;
    readonly newPoints: readonly IPoint[];
};

export type SelectablePoint = {
    readonly pointIndex: number;
    readonly originCursorCoordinates: IPoint;
    readonly linkIndex: number;
    readonly lastPosition: IPoint;
};

export type AnchorType = {
    readonly element: HTMLElement;
    readonly isSelected: boolean;
};

export type ScrollPosition = Readonly<Required<Pick<ScrollToOptions, "left" | "top">>>;

export type SelectedAnchorsProperties = "nAnchor" | "neAnchor" | "eAnchor" | "seAnchor" | "sAnchor" | "swAnchor" | "wAnchor" | "nwAnchor";

export type SelectedAnchorsType = Record<SelectedAnchorsProperties, AnchorType>;

export interface IPicassoState {
    readonly components: readonly IComponent[];
    readonly selectedComponentsIndexes: readonly number[];
    readonly links: readonly ILink[];
    readonly selectedLinksIndexes: readonly number[];
    readonly selectedPoint: SelectablePoint | null;
    readonly scaleIndex: number;
    readonly requestedScrollPosition: ScrollPosition;
}

export type PicassoEditorProps = {
    /** Klíč instance Picassa pro přístup k reduxímu storu. */
    readonly picassoInstanceKey: string;
};

/** Obalovací funkce pro MapStateToProps pro extrakci podstromu se stavem Picassa. */
export type WrapMapStateToProps<S extends ObjectType> = <SP, OP>(mapStateToProps: MapStateToProps<SP, OP, IPicassoState>) => MapStateToProps<SP, OP, S>;

/**
 * Typ pro prázdný objekt.
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export type EmptyObjectType = {};

/**
 * Typ pro objekt. Typ `object` by se nedoporučuje používat.
 * @see {@link https://github.com/microsoft/TypeScript/issues/21732}
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export type ObjectType = object;

/**
 * Helper type that makes all properties of given type {@link T} mutable (a.k.a. removes readonly modifier).
 * @template T The type that has readonly properties.
 */
export type Mutable<T> = {
    -readonly [Key in keyof T]: T[Key];
};

//#region Action types
export interface IPicassoAction extends Action<string> {
    readonly meta: {
        readonly picassoInstanceKey: string;
    };
}

export class IPicassoAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance?.meta?.picassoInstanceKey;
    }
}

export interface ILoadStateAction extends IPicassoAction {
    readonly payload: {
        readonly components: readonly IComponent[];
        readonly links: readonly ILink[];
        readonly selectedComponentIndex?: number;
        readonly selectedLinkIndex?: number;
    };
}

export class ILoadStateAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === LOAD_PICASSO_STATE;
    }
}

export interface ISelectComponentAction extends IPicassoAction {
    readonly payload: {
        readonly component: IComponent;
        readonly reselect: boolean;
    };
}

export class ISelectComponentAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SELECT_COMPONENT;
    }
}

export interface IUnselectComponentAction extends IPicassoAction {
    readonly payload: {
        readonly componentIndex: number;
    };
}

export class IUnselectComponentAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === UNSELECT_COMPONENT;
    }
}

export interface ISelectLinkAction extends IPicassoAction {
    readonly payload: {
        readonly link: ILink;
        readonly reselect: boolean;
    };
}

export class ISelectLinkAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SELECT_LINK;
    }
}

export interface IUnselectLinkAction extends IPicassoAction {
    readonly payload: {
        readonly linkIndex: number;
    };
}

export class IUnselectLinkAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === UNSELECT_LINK;
    }
}

export interface IUpdateComponentsAction extends IPicassoAction {
    readonly payload: {
        readonly changedComps: readonly ComponentChangeType[];
    };
}

export class IUpdateComponentsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === UPDATE_COMPONENTS;
    }
}

export interface IDeleteComponentsAction extends IPicassoAction {
    readonly payload: {
        readonly componentsToDelete: readonly number[];
    };
}

export class IDeleteComponentsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === DELETE_COMPONENTS;
    }
}

export interface IUpdateLinksAction extends IPicassoAction {
    readonly payload: {
        readonly changedLinks: readonly LinkChangeType[];
    };
}

export class IUpdateLinksAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === UPDATE_LINKS;
    }
}

export interface ISelectPointAction extends IPicassoAction {
    readonly payload: {
        readonly selectedPoint: SelectablePoint | null;
    };
}

export class ISelectPointAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SELECT_POINT;
    }
}

export interface IUnselectPointAction extends IPicassoAction { }

export class IUnselectPointAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === UNSELECT_POINT;
    }
}

export interface IChangeScaleIndexAction extends IPicassoAction {
    readonly payload: {
        readonly newScaleIndex: number;
    };
}

export class IChangeScaleIndexAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === CHANGE_SCALE_INDEX;
    }
}

export interface ISelectAllComponentsAction extends IPicassoAction {
    readonly payload: {
        readonly parentIndex: number;
    };
}

export class ISelectAllComponentsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SELECT_ALL_COMPONENTS;
    }
}

export interface IUnselectAllAction extends IPicassoAction { }

export class IUnselectAllAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === UNSELECT_ALL;
    }
}

export interface IRequestPaintingScrollPositionAction extends IPicassoAction {
    readonly payload: {
        readonly requestedScrollPosition: ScrollPosition;
    };
}

export class IRequestPaintingScrollPositionAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === REQUEST_PAINTING_SCROLL_POSITION;
    }
}

export interface IUpdateComponentVisibilityAction extends IPicassoAction {
    readonly payload: {
        readonly componentIndex: number;
        readonly isVisible: boolean;
    };
}

export class IUpdateComponentVisibilityAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === UPDATE_COMPONENT_VISIBILITY;
    }
}

export interface IUpdateComponentsAlignmentAction extends IPicassoAction {
    readonly payload: {
        readonly componentIndex: number;
        readonly align: AlignType;
    };
}

export class IUpdateComponentsAlignmentAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === UPDATE_COMPONENTS_ALIGNMENT;
    }
}

export interface IUpdateComponentsResizeOptionAction extends IPicassoAction {
    readonly payload: {
        readonly componentIndex: number;
        readonly resizeOption: ResizeOptions;
        readonly noCssHeight: boolean;
        readonly noCssWidth: boolean;
    };
}

export class IUpdateComponentsResizeOptionAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === UPDATE_COMPONENTS_RESIZE_OPTION;
    }
}

//#endregion
